
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getHolidayById, updateHoliday } from 'adminApi/LeaveApi/HolidayApi';
import { validateForm } from "utils/validation";
import { HolidayRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditHolidayForm from "./Components/EditHolidayForm";
import fields from "../json/HolidayFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";

const EditHoliday = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { formdata } = fields;

    // Initialize form values from JSON fields
    const initialValues = formdata.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});

    const { values, errors, handleChange, setValues } = useFormChange(
        initialValues,
        HolidayRules
    );

    // Auto-calculate total_days and sync dates
    useEffect(() => {
        if (values.start_date) {
            const start = new Date(values.start_date);
            const end = values.end_date ? new Date(values.end_date) : null;

            // If end_date is missing or before start_date, default it to start_date
            if (!values.end_date || (end && end < start)) {
                setValues(prev => ({
                    ...prev,
                    end_date: values.start_date,
                    total_days: 1
                }));
                return;
            }

            if (end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
                const diffTime = end - start;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                setValues(prev => ({ ...prev, total_days: Math.max(diffDays, 1) }));
            }
        } else {
            setValues(prev => ({ ...prev, total_days: 0 }));
        }
    }, [values.start_date, values.end_date, setValues]);

    // Fetch existing data by ID
    const { loading: loadingFetch } = useFetchFormDataById(
        id,
        formdata,
        getHolidayById,
        setValues
    );

    // Form submit
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => formData.append(key, val ?? ""));
            return await updateHoliday({ id, data: formData });
        },
        (vals) => validateForm(vals, HolidayRules),
        () => navigate(-1),
        "Holiday updated successfully"
    );

    return (
        <>
            {(loadingFetch || loadingSubmit) && <Spinner isSpinner={true} />}
            <EditHolidayForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
            />
        </>
    );
};

export default EditHoliday;
